import { FC, useMemo } from 'react'
import cn from 'classnames'
import styles from 'components/atoms/ModuleLayout/ModuleLayout.styl'
import { ReactNode } from 'react-redux'
import Flex, { FlexProps } from 'components/atoms/Flex/Flex'
import { QAMarked } from 'interfaces/component'
import ModuleHeader from 'components/features/pfm/ModuleWrapper/ModuleHeader'
import { AnchorPlacement } from 'interfaces/navigation'
import { PageAnchorHandler } from 'handlers/PageAnchorHandler/PageAnchorHandler'
import { useVariants } from 'hooks/useVariants'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { State } from 'interfaces/state'

export type HeaderStyles = 'connected' | 'nested' | 'floating'

type ModuleLayoutProps =
  QAMarked &
  FlexProps & {
  anchor?: AnchorPlacement
  headerStyle?: HeaderStyles
  cardLayout?: boolean
  className?: string
  contentClassName?: string
  connected?: boolean
  title?: ReactNode
  titleLinkTo?: AnchorPlacement
  subtitle?: string
  hint?: ReactNode
  action?: ReactNode
  defaultVariant?: number // For Dev Only
}

/**
 * @param {number} defaultVariant Use this param in dev only. Pass different variations of this module as children
 * and set a 'defaultVariant' to turn on the Variant switcher.
 */
const ModuleLayout: FC<ModuleLayoutProps> = (props) => {
  const preferences = useStoreSelector((state: State) => state.data.preferences)
  const {
    anchor,
    headerStyle = preferences.headerStyle || 'floating',
    cardLayout = true,
    className,
    contentClassName,
    children,

    hint,
    title,
    subtitle,
    action,
    titleLinkTo,

    qaType,
    qaMarker,

    theme,
    defaultVariant, // For dev/design use
    ...flexProps
  } = props

  // ! defaultVariant - FOR DEV ONLY ----------------------
  // Used in development to try out different variations of a module
  const headerIsConnected = headerStyle === 'connected'
  const shouldUseVariants = !!defaultVariant && Array.isArray(children) && children.length > 0
  const variants = useMemo(() => shouldUseVariants ? children : [], [children, shouldUseVariants])
  const { Variants, VariantButtons, variantsContext } = useVariants(variants as Array<ReactNode>, defaultVariant)

  const variantButtons = useMemo(() => !!shouldUseVariants &&
    <VariantButtons { ...variantsContext } />
  , [VariantButtons, shouldUseVariants, variantsContext])

  const variantChildren = useMemo(() => !!shouldUseVariants &&
    <Variants { ...variantsContext } />
  , [Variants, shouldUseVariants, variantsContext])

  // ! End FOR DEV ONLY -----------------------------------------

  const headerChild = useMemo(() => {
    if (!title && !hint && !subtitle) {
      return null
    }
    return (
      <ModuleHeader
        hint={ hint }
        title={ title }
        subtitle={ subtitle }
        connected={ headerIsConnected }
        action={ variantButtons || action } // Show variants in development
        titleLinkTo={ titleLinkTo }
        responsive
      />
    )
  }, [title, hint, subtitle, headerIsConnected, variantButtons, action, titleLinkTo])

  const contentClassNames = cn(
    contentClassName,
    styles.content, {
      [styles.cardLayout]: !!cardLayout,
    }
  )

  const contentChild = useMemo(() => (
    <>
      { headerStyle !== 'nested' && headerChild }
      <Flex
        className={ contentClassNames }
        theme={ cardLayout ? 'card' : theme }
        padding={ cardLayout ? 'defaultSize' : 'none' }
        { ...flexProps }
      >
        { headerStyle === 'nested' && headerChild }
        { variantChildren || children }
      </Flex>
    </>
  ), [headerStyle, headerChild, contentClassNames, cardLayout, theme, flexProps, variantChildren, children])

  const moduleProps = {
    className: cn(
      className,
      styles.moduleLayout, {
        [styles.connectedToHeader]: headerIsConnected,
      }
    ),
    'data-qa-type': qaType,
    'data-qa-marker': qaMarker,
  }

  return !!anchor ? (
    <PageAnchorHandler { ...moduleProps } placement={ anchor } component='article'>
      { contentChild }
    </PageAnchorHandler>
  ) : (
    <article { ...moduleProps }>
      { contentChild }
    </article>
  )
}

export default ModuleLayout
